import React, { createContext, useContext, useEffect, useRef, useState } from 'react';
import {
  Portfolio, Quote, Trade,
  getQuotes, tickPrices, loadPortfolio, savePortfolio, resetPortfolio,
} from '../api/market';
import { STARTING_CASH } from '../config';

interface TradingState {
  quotes: Quote[];
  portfolio: Portfolio;
  buy: (symbol: string, shares: number) => string | null;
  sell: (symbol: string, shares: number) => string | null;
  reset: () => void;
  totalValue: number;
  totalPnl: number;
  totalPnlPct: number;
}

const Ctx = createContext<TradingState | null>(null);

export function TradingProvider({ children }: { children: React.ReactNode }) {
  const [quotes, setQuotes] = useState<Quote[]>(getQuotes);
  const [portfolio, setPortfolio] = useState<Portfolio>(loadPortfolio);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  useEffect(() => {
    intervalRef.current = setInterval(() => {
      tickPrices();
      setQuotes(getQuotes());
    }, 3000);
    return () => { if (intervalRef.current) clearInterval(intervalRef.current); };
  }, []);

  function updatePortfolio(p: Portfolio) {
    setPortfolio(p);
    savePortfolio(p);
  }

  function buy(symbol: string, shares: number): string | null {
    const q = quotes.find(q => q.symbol === symbol);
    if (!q) return 'Stock not found';
    const total = q.price * shares;
    if (total > portfolio.cash) return `Insufficient funds (need $${total.toFixed(2)})`;

    const trade: Trade = {
      id: crypto.randomUUID(),
      symbol, side: 'buy', shares, price: q.price,
      total, timestamp: new Date().toISOString(),
    };

    const existing = portfolio.positions.find(p => p.symbol === symbol);
    let positions = portfolio.positions.filter(p => p.symbol !== symbol);
    if (existing) {
      const newShares = existing.shares + shares;
      const newAvg = (existing.avgCost * existing.shares + total) / newShares;
      positions = [...positions, { symbol, shares: newShares, avgCost: +newAvg.toFixed(4) }];
    } else {
      positions = [...positions, { symbol, shares, avgCost: q.price }];
    }

    updatePortfolio({
      cash: +(portfolio.cash - total).toFixed(2),
      positions,
      trades: [trade, ...portfolio.trades],
    });
    return null;
  }

  function sell(symbol: string, shares: number): string | null {
    const q = quotes.find(q => q.symbol === symbol);
    if (!q) return 'Stock not found';
    const pos = portfolio.positions.find(p => p.symbol === symbol);
    if (!pos || pos.shares < shares) return `Insufficient shares (have ${pos?.shares ?? 0})`;

    const total = q.price * shares;
    const trade: Trade = {
      id: crypto.randomUUID(),
      symbol, side: 'sell', shares, price: q.price,
      total, timestamp: new Date().toISOString(),
    };

    let positions = portfolio.positions.filter(p => p.symbol !== symbol);
    if (pos.shares > shares) {
      positions = [...positions, { symbol, shares: pos.shares - shares, avgCost: pos.avgCost }];
    }

    updatePortfolio({
      cash: +(portfolio.cash + total).toFixed(2),
      positions,
      trades: [trade, ...portfolio.trades],
    });
    return null;
  }

  function reset() {
    resetPortfolio();
    setPortfolio({ cash: STARTING_CASH, positions: [], trades: [] });
  }

  const positionsValue = portfolio.positions.reduce((sum, pos) => {
    const q = quotes.find(q => q.symbol === pos.symbol);
    return sum + (q ? q.price * pos.shares : pos.avgCost * pos.shares);
  }, 0);
  const totalValue = portfolio.cash + positionsValue;
  const totalPnl = totalValue - STARTING_CASH;
  const totalPnlPct = (totalPnl / STARTING_CASH) * 100;

  return (
    <Ctx.Provider value={{ quotes, portfolio, buy, sell, reset, totalValue, totalPnl, totalPnlPct }}>
      {children}
    </Ctx.Provider>
  );
}

export const useTrading = () => {
  const ctx = useContext(Ctx);
  if (!ctx) throw new Error('useTrading must be inside TradingProvider');
  return ctx;
};
